import { lazy, Suspense, useCallback } from "react";
import type { ComponentType } from "react";
import { useHistory } from "react-router-dom";
import { Button, Flex, Popover } from "orcs-design-system";

import PersonContactDetails from "src/components/PersonContactDetails";
import useRestricted from "src/app/Restricted/useRestricted";
import { PERSON_TAB } from "src/consts/urlPaths";
import { useAllocationProject } from "src/contexts/global/GlobalContext";
import {
  useAllocationConfig,
  useGroupTypes,
  useWorkspace,
} from "src/contexts/global/WorkspaceContext";
import { useModal } from "src/contexts/withModal";
import { personDetailsPath } from "src/util/routing";

// Loaded lazily: the allocations modal pulls in the membership editor, which
// imports back into src/util/search.js and would close an import cycle here.
const PersonAllocationsModal = lazy(
  () => import("src/components/Person/AllocationsModal")
) as unknown as ComponentType<Record<string, unknown>>;

type SearchPerson = {
  aggregateId: string;
};

type PersonSearchActionsProps = {
  person: SearchPerson;
};

const PersonSearchActions = ({ person }: PersonSearchActionsProps) => {
  // The global search dropdown lives in the app header, above the modal
  // provider, so it falls back to the person's allocations tab.
  const dispatch = useModal()?.[1];
  const history = useHistory();
  const [, isPowerUser, isAdminUser] = useRestricted();
  const canManagePeople = isPowerUser || isAdminUser;
  const { disableIndividualAllocation } = useAllocationConfig();
  const workspace = useWorkspace();
  const groupTypes = useGroupTypes();
  const allocationProject = useAllocationProject();

  const onEditAllocations = useCallback(() => {
    if (!dispatch) {
      history.push(personDetailsPath(person, PERSON_TAB.ALLOCATIONS));
      return;
    }

    const onHideModal = () =>
      dispatch({ type: "setModalVisible", isModalVisible: false });

    dispatch({
      type: "setModalComponent",
      modalComponent: (
        <Suspense fallback={null}>
          <PersonAllocationsModal
            isVisible={true}
            // The modal mutates the person it is given, so it gets its own copy.
            person={{ ...person }}
            workspace={workspace}
            groupTypes={groupTypes}
            allocationProject={allocationProject}
            onHideModal={onHideModal}
            forceUpdatePerson
          />
        </Suspense>
      ),
    });
  }, [dispatch, history, person, workspace, groupTypes, allocationProject]);

  return (
    <Flex alignItems="center" gap="s">
      <PersonContactDetails person={person} tooltipDirection="left" />
      {canManagePeople && !disableIndividualAllocation && (
        <Popover
          direction="left"
          width="160px"
          textAlign="center"
          text="Edit person allocations"
        >
          <Button
            data-testid="search-person-allocate"
            small
            onClick={onEditAllocations}
          >
            Allocate
          </Button>
        </Popover>
      )}
    </Flex>
  );
};

export default PersonSearchActions;
